import { formBuilderFromDocument } from "./generate-form-layout";
import { DocumentFormSource } from "./DocumentFormSource";
import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import {
  uid,
  resourceKindSchema,
  captureTextStyle,
  applyResourceSnapshot,
  createOutputProfile,
  newField,
  reusableFormSchema,
  suggestFormFromDocument,
  suggestFormMapping,
  formFieldCompatible,
  exposeFormInput,
  parseResourceValue,
  resourceAvailableToProject,
  filterResources,
  globalOutputSettings,
  type Repository,
  type Project,
  type Resource,
  type ResourceKind,
  type TenantSettings,
} from "@proof/core";
import { PolicySettingsEditor } from "./FieldPolicies";
import { DatasetEditor, newDataset } from "./DatasetEditor";
import { JsonImportButton } from "./json-import/JsonImport";
import { PrintProfileControl } from "./Outputs";
import { SheetSettings } from "./SheetSettings";
import { InsetsControl } from "./InsetsControl";
import { PageMarks } from "./PageMarks";
import { impositionSchema, productionSchema } from "@proof/core";
import {
  FormLibraryEditor,
  RuleLibraryEditor,
  PresetLibraryEditor,
} from "./LibraryEditors";
import { Select,
  Button,
  Control,
  JsonEditor,
  Modal,
  SetupModes,
  type SetupMode,
} from "./components";
const labels: Record<ResourceKind, string> = {
  form: "reusable form",
  rule: "visibility rule",
  preset: "value preset",
  "page-size": "page size",
  production: "page bleed profile",
  output: "output profile",
  dataset: "dataset",
  style: "text style",
  sheet: "sheet / paper preset",
  validation: "validation",
  format: "format",
  font: "managed font",
};
function GlobalFormAssignment({
  project,
  resource,
  onClose,
  onApply,
}: {
  project: Project;
  resource: Resource;
  onClose: () => void;
  onApply: (project: Project) => void;
}) {
  const form = reusableFormSchema.parse(resource.value);
  const suggested = useMemo(
    () => suggestFormMapping(form.fields, project.fields),
    [resource, project],
  );
  const [mapping, setMapping] = useState(suggested.mapping);
  const [exposed, setExposed] = useState<string[]>([]);
  return (
    <Modal
      title={resource.projectId ? "Assign project form" : "Assign global form"}
      wide
      onClose={onClose}
    >
      <p>
        {resource.name} · v{resource.version}. Only mapped inputs appear by
        default. Expose additional inputs to collect values without placing them
        in the artwork.
      </p>
      {form.fields.map((input) => (
        <article className="part-card" key={input.id}>
          <Control
            label={`Map ${input.label} to variable`}
            note={
              suggested.suggestions.find((s) => s.inputId === input.id)?.reason
            }
          >
            <Select
              value={mapping[input.id] || ""}
              onChange={(e) => {
                const next = { ...mapping };
                if (e.target.value) next[input.id] = e.target.value;
                else delete next[input.id];
                setMapping(next);
              }}
            >
              <option value="">Unmapped — hidden</option>
              {project.fields
                .filter((f) => formFieldCompatible(input, f))
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label} ({f.key})
                  </option>
                ))}
            </Select>
          </Control>
          {!mapping[input.id] && (
            <label>
              <input
                type="checkbox"
                checked={exposed.includes(input.id)}
                onChange={(e) =>
                  setExposed(
                    e.target.checked
                      ? [...exposed, input.id]
                      : exposed.filter((id) => id !== input.id),
                  )
                }
              />{" "}
              Expose {input.label} without artwork
            </label>
          )}
        </article>
      ))}
      <div className="button-row">
        <Button onClick={onClose}>Cancel assignment</Button>
        <Button
          kind="primary"
          onClick={() => {
            let p = applyResourceSnapshot(project, resource, {
              formApplication: "mapped",
            });
            const instance = p.formInstances.at(-1)!;
            instance.mapping = mapping;
            for (const inputId of exposed.filter((id) => !mapping[id]))
              p = exposeFormInput(p, instance.id, inputId);
            onApply(p);
          }}
        >
          Assign form
        </Button>
      </div>
    </Modal>
  );
}
export function CatalogManager({
  repository,
  project,
  onChange,
  notify,
  kinds = resourceKindSchema.options,
  permissions = { manage: true, apply: true, policy: true },
  initialScope = "shared",
  allowProjectScope = true,
}: {
  repository: Pick<
    Repository,
    | "resources"
    | "tenantSettings"
    | "saveResource"
    | "saveTenantSettings"
    | "archiveResource"
  >;
  kinds?: ResourceKind[];
  initialScope?: "project" | "shared";
  allowProjectScope?: boolean;
  permissions?: { manage: boolean; apply: boolean; policy: boolean };
  project: Project;
  onChange: (p: Project) => void;
  notify: (s: string) => void;
}) {
  const [scope, setScope] = useState(initialScope);
  const identity = `${project.tenantId}:${project.id}:${scope}`;
  const session = useRef({ identity });
  if (session.current.identity !== identity) session.current = { identity };
  const currentSession = session.current;
  const mounted = useRef(true);
  const [resources, setResources] = useState<Resource[]>([]),
    [policy, setPolicy] = useState<TenantSettings>(),
    [kind, setKind] = useState<ResourceKind>(
      kinds.includes("page-size") ? "page-size" : kinds[0] || "form",
    ),
    [name, setName] = useState(""),
    [editing, setEditing] = useState<Resource>(),
    [draft, setDraft] = useState<unknown>(),
    [search, setSearch] = useState(""),
    [filter, setFilter] = useState("all"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [expectedVersion, setExpectedVersion] = useState(0);
  const [editorGeneration, setEditorGeneration] = useState(0);
  const [assignment, setAssignment] = useState<Resource>();
  // The setup panel opens only while creating or editing an entry.
  const [setupOpen, setSetupOpen] = useState(false),
    [setupMode, setSetupMode] = useState<SetupMode>("guided");
  const refresh = async () => {
    const [rows, settings] = await Promise.all([
      repository.resources(project.tenantId),
      permissions.policy
        ? repository.tenantSettings(project.tenantId)
        : Promise.resolve(undefined),
    ]);
    if (!mounted.current || session.current !== currentSession) return;
    setResources(
      rows.filter(
        (r) => kinds.includes(r.kind) && resourceAvailableToProject(r, project),
      ),
    );
    setPolicy(settings);
  };
  useEffect(() => {
    mounted.current = true;
    void refresh().catch((e) => notify(e.message));
    return () => {
      mounted.current = false;
    };
  }, [repository, identity]);
  const perform = (operation: () => Promise<unknown>) =>
    void operation()
      .then(refresh)
      .catch((e) => notify(e.message));
  const scopedResources = filterResources(
    resources.filter((r) => r.kind !== "output"),
    {
      scope,
      projectId: project.id,
    },
  ).concat(resources.filter((r) => r.kind === "output" && !r.projectId));
  const latest = scopedResources
    .filter(
      (row) =>
        row.kind !== "form" ||
        reusableFormSchema.parse(row.value).purpose !== "profile-collection",
    )
    .filter(
      (r) =>
        !resources.some(
          (other) => other.id === r.id && other.version > r.version,
        ),
    );
  const sample = useMemo(
    () =>
      kind === "form"
        ? { application: "mapped", fields: [newField("text")] }
        : kind === "rule"
          ? {
              sourceKey: project.fields[0]?.key || "variable",
              value: "",
              show: [],
              hide: [],
              clear: [],
            }
          : kind === "preset"
            ? {
                options: [
                  { id: uid("option"), label: "New option", values: {} },
                ],
              }
            : kind === "page-size"
              ? { width: 612, height: 792, margin: 36 }
              : kind === "production"
                ? project.pages[0].production
                : kind === "output"
                  ? project.outputs[0]
                  : kind === "style"
                    ? captureTextStyle(project, project.styles[0])
                    : kind === "sheet"
                      ? impositionSchema.parse({
                          enabled: true,
                          gridMode: "auto",
                          rotation: "auto",
                          sheetWidth: 936,
                          sheetHeight: 1368,
                          stock: {
                            material: "Paper",
                            color: "White",
                            currency: "USD",
                          },
                        })
                      : kind === "dataset"
                        ? newDataset()
                        : kind === "validation"
                          ? newField().validation
                          : kind === "format"
                            ? newField().format
                            : {
                                faceId: project.fontManifest[0]?.id || "",
                                fontManifest: project.fontManifest,
                              },
    [kind, project],
  );
  const value = draft ?? editing?.value ?? sample;
  const visual = [
    "validation",
    "format",
    "form",
    "rule",
    "preset",
    "dataset",
    "production",
    "sheet",
  ].includes(kind);
  const save = async (value: unknown) => {
    if (!permissions.manage) {
      notify("Resource management is not available for this account");
      return false;
    }
    if (busy) return false;
    setBusy(true);
    setError("");
    try {
      if (!name.trim()) throw new Error("Enter a resource name");
      if (kind === "form") {
        const form = reusableFormSchema.parse(value),
          link = form.profileSource;
        if (link) {
          const source = resources.find(
            (r) =>
              r.kind === "form" &&
              r.id === link.id &&
              r.version === link.version &&
              r.id !== editing?.id,
          );
          if (
            !source ||
            (scope === "shared" && source.projectId) ||
            !Object.keys(link.mapping).length
          )
            throw new Error(
              "Select an available profile collection and map at least one input",
            );
          const sourceForm = reusableFormSchema.parse(source.value);
          if (
            sourceForm.profileSource ||
            !Object.entries(link.mapping).every(([local, remote]) => {
              const a = form.fields.find((f) => f.id === local),
                b = sourceForm.fields.find((f) => f.id === remote);
              return a && b && formFieldCompatible(a, b);
            })
          )
            throw new Error(
              "Review the profile mapping: each value must match a compatible input in the original collection",
            );
        }
      }
      const row = await repository.saveResource(project.tenantId, {
        id: editing?.id || uid("resource"),
        tenantId: project.tenantId,
        ...(scope === "project" && kind !== "output"
          ? { projectId: project.id }
          : {}),
        kind,
        name: name.trim(),
        version: expectedVersion,
        value:
          kind === "style"
            ? captureTextStyle(project, value)
            : kind === "output"
              ? globalOutputSettings(value as Project["outputs"][number])
              : value,
      });
      setEditing(row);
      setExpectedVersion(row.version);
      setDraft(row.value);
      await refresh();
      notify(`Catalog version ${row.version} saved.`);
      return true;
    } catch (e) {
      setError((e as Error).message);
      notify((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const start = (kind: ResourceKind) => {
    setEditorGeneration((current) => current + 1);
    setEditing(undefined);
    setExpectedVersion(0);
    setDraft(kind === "dataset" ? newDataset() : undefined);
    setName("");
    setKind(kind);
    setError("");
    setSetupOpen(true);
    setSetupMode("guided");
  };
  const edit = (row: Resource) => {
    setEditorGeneration((current) => current + 1);
    setEditing(row);
    setExpectedVersion(
      Math.max(
        ...resources.filter((r) => r.id === row.id).map((r) => r.version),
      ),
    );
    setDraft(row.value);
    setName(row.name);
    setKind(row.kind);
    setError("");
    setSetupOpen(true);
    setSetupMode("guided");
  };
  const apply = (row: Resource) => {
    try {
      if (
        row.kind === "form" &&
        reusableFormSchema.parse(row.value).application !== "copy"
      ) {
        setAssignment(row);
        return;
      }
      onChange(applyResourceSnapshot(project, row));
      notify(`Copied ${row.name} v${row.version} into this draft.`);
    } catch (e) {
      notify((e as Error).message);
    }
  };
  const download = (row: Resource) => {
    const url = URL.createObjectURL(
      new Blob(
        [
          JSON.stringify(
            { kind: row.kind, name: row.name, value: row.value },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      ),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `library-${row.kind}-v${row.version}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const canShare = (row: Resource) => {
    if (row.kind !== "form") return true;
    const source = reusableFormSchema.parse(row.value).profileSource;
    return (
      !source ||
      !resources.find((candidate) => candidate.id === source.id)?.projectId
    );
  };
  const creatable =
    kinds.length > 4
      ? (["form", "rule", "preset", "dataset"] as ResourceKind[]).filter((k) =>
          kinds.includes(k),
        )
      : kinds;
  const visualSetup = visual || kind === "output";
  const close = () => {
    setSetupOpen(false);
    setEditing(undefined);
    setDraft(undefined);
    setName("");
    setError("");
  };
  return (
    <section className="catalog-manager">
      <Control
        label="Configuration scope"
        note={
          allowProjectScope
            ? `Shared items can be reused by every project. Project items belong only to ${project.name}.`
            : "Open a project to keep items private to it."
        }
      >
        <Select
          value={scope}
          onChange={(event) => {
            setScope(event.target.value as "project" | "shared");
            start(kind);
            setSearch("");
            setFilter("all");
          }}
        >
          <option value="project" disabled={!allowProjectScope}>
            This project · {project.name}
          </option>
          <option value="shared">Shared library</option>
        </Select>
      </Control>
      <fieldset className="library-editor-shell" disabled={busy}>
        <div className="catalog-actions">
          <div>
            <h3>
              {kinds.length === 1 && kinds[0] === "output"
                ? "Global output profiles"
                : scope === "project"
                  ? "Project building blocks"
                  : "Shared building blocks"}
            </h3>
            <p className="muted">
              {kinds.length === 1 && kinds[0] === "output"
                ? "Output profiles are global. Assign a saved version to a project's output stage."
                : scope === "project"
                  ? `New entries are saved only for ${project.name}.`
                  : "New entries are shared across this workspace."}{" "}
              Projects keep the version they apply; later edits do not change
              them.
              {kinds.length === 1 &&
                kinds[0] === "dataset" &&
                " Save each dataset as its own entry, then apply any number of datasets to the same document."}
            </p>
          </div>
          <div className="button-row">
            {creatable.map((k, index) => (
              <Button
                key={k}
                kind={index === 0 ? "primary" : ""}
                disabled={busy || !permissions.manage}
                onClick={() => start(k)}
              >
                <Plus size={14} />
                Create {labels[k]}
              </Button>
            ))}
            {kinds.length > creatable.length && (
              <Button
                disabled={busy || !permissions.manage}
                onClick={() =>
                  start(
                    kinds.includes("page-size")
                      ? "page-size"
                      : kinds.find((k) => !creatable.includes(k))!,
                  )
                }
              >
                Create another kind
              </Button>
            )}
            {kinds.includes("form") && (
              <DocumentFormSource
                project={project}
                onSelect={(source, linked) => {
                  try {
                    const suggestion = suggestFormFromDocument(source);
                    start("form");
                    setName(`${source.name} form`);
                    setScope("shared");
                    setDraft({
                      ...suggestion.value,
                      extra_json_data: {
                        version: 1,
                        builder: formBuilderFromDocument(source),
                        linkedProjectIds: linked ? [source.id] : [],
                      },
                    });
                    notify(
                      `Generated ${suggestion.value.fields.length} inputs for review. ${suggestion.skipped.length} dependent inputs are retained in the extended builder. Save a shared library version to keep it.`,
                    );
                  } catch (error) {
                    notify((error as Error).message);
                  }
                }}
              />
            )}
            <JsonImportButton
              label="Import library entry"
              title="Import a library entry"
              review={false}
              disabled={busy || !permissions.manage}
              maxBytes={6_100_000}
              description={
                <p>
                  Choose a library entry exported from Proof Studio. It opens in
                  the editor below so you can check it — nothing is stored until
                  you save.
                </p>
              }
              sample={
                kinds.includes("dataset")
                  ? {
                      fileName: "example-dataset.json",
                      value: {
                        kind: "dataset",
                        name: "Example cities",
                        value: {
                          id: "cities",
                          name: "Cities",
                          choices: [{ value: "chi", label: "Chicago" }],
                        },
                      },
                    }
                  : undefined
              }
              inspect={(raw) => {
                const entry = raw as {
                  kind?: unknown;
                  name?: unknown;
                  value?: unknown;
                };
                if (!entry || typeof entry !== "object" || Array.isArray(entry))
                  throw new Error(
                    'A library entry is an object with "kind", "name" and "value".',
                  );
                const parsedKind = resourceKindSchema.safeParse(entry.kind);
                if (!parsedKind.success)
                  throw new Error(
                    `"kind" must name a library type such as ${kinds.map((k) => `"${k}"`).join(", ")}.`,
                  );
                const k = parsedKind.data;
                if (!kinds.includes(k))
                  throw new Error(
                    `This file holds a ${labels[k].toLowerCase()}. Open ${labels[k]} under Configuration to import it there.`,
                  );
                try {
                  parseResourceValue(k, entry.value);
                } catch (error) {
                  const issues = (error as { issues?: { path: unknown[] }[] })
                    .issues;
                  if (issues)
                    throw {
                      issues: issues.map((issue) => ({
                        ...issue,
                        path: ["value", ...issue.path],
                      })),
                    };
                  throw error;
                }
                return {
                  title:
                    typeof entry.name === "string" ? entry.name : labels[k],
                };
              }}
              onConfirm={(raw) => {
                const entry = raw as {
                  kind: ResourceKind;
                  name?: unknown;
                  value: unknown;
                };
                start(entry.kind);
                setName(typeof entry.name === "string" ? entry.name : "");
                setDraft(parseResourceValue(entry.kind, entry.value));
                notify("Imported library entry for review. Save to store it.");
              }}
            />
          </div>
        </div>
        {setupOpen && (
          <fieldset
            className="library-editor-shell catalog-setup"
            disabled={!permissions.manage}
            aria-label="Library entry setup"
          >
            <header className="catalog-setup-header">
              <div>
                <h3>
                  {editing
                    ? `Editing ${editing.name} from v${editing.version}`
                    : `New ${labels[kind]}`}
                </h3>
                <p className="muted">
                  {editing
                    ? `Saving creates version ${expectedVersion + 1}. Projects using older versions are unchanged.`
                    : "Name it, set it up, then save it to the library."}
                </p>
              </div>
              <div className="button-row">
                {editing && (
                  <Button onClick={() => start(kind)}>
                    {kind === "dataset"
                      ? "Add another dataset"
                      : "New catalog entry"}
                  </Button>
                )}
                <Button onClick={close}>Close setup</Button>
              </div>
            </header>
            <ol className="setup-flow">
              <li>
                <span className="setup-step-number" aria-hidden="true">
                  1
                </span>
                <div className="setup-step-body">
                  <h4>Name it</h4>
                  <div className="control-grid">
                    <Control label="Resource name">
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Control>
                    {kinds.length > 1 && (
                    <Control label="Resource kind">
                      <Select
                        value={kind}
                        disabled={!!editing || busy}
                        onChange={(e) => {
                          setKind(e.target.value as ResourceKind);
                          setDraft(undefined);
                          setError("");
                        }}
                      >
                        {kinds.map((kind) => (
                          <option key={kind} value={kind}>
                            {labels[kind]}
                          </option>
                        ))}
                      </Select>
                    </Control>
                    )}
                  </div>
                </div>
              </li>
              <li>
                <span className="setup-step-number" aria-hidden="true">
                  2
                </span>
                <div className="setup-step-body">
                  <div className="setup-step-heading">
                    <h4>Set it up</h4>
                    {visualSetup && (
                      <SetupModes
                        label="Library entry setup mode"
                        guidedLabel="Visual setup"
                        modes={["guided", "json"]}
                        value={setupMode}
                        onChange={setSetupMode}
                      />
                    )}
                  </div>
                  {visualSetup && setupMode === "guided" ? (
                    <>
                      {kind === "form" && (
                        <>
                          <FormLibraryEditor
                            value={value}
                            onChange={setDraft}
                          />
                          <Control
                            label="Global profile collection"
                            note="Use this form’s own reusable profiles, or map another saved form’s profile collection. Linked collections are read-only here; edit profiles through their original form or Your profiles."
                          >
                            <Select
                              value={
                                (
                                  value as ReturnType<
                                    typeof reusableFormSchema.parse
                                  >
                                ).profileSource
                                  ? JSON.stringify([
                                      (
                                        value as ReturnType<
                                          typeof reusableFormSchema.parse
                                        >
                                      ).profileSource!.id,
                                      (
                                        value as ReturnType<
                                          typeof reusableFormSchema.parse
                                        >
                                      ).profileSource!.version,
                                    ])
                                  : ""
                              }
                              onChange={(e) => {
                                const form = value as ReturnType<
                                  typeof reusableFormSchema.parse
                                >;
                                const source = resources.find(
                                  (r) =>
                                    JSON.stringify([r.id, r.version]) ===
                                    e.target.value,
                                );
                                setDraft({
                                  ...form,
                                  application: "mapped",
                                  profileSource: source
                                    ? {
                                        id: source.id,
                                        version: source.version,
                                        mapping: suggestFormMapping(
                                          form.fields,
                                          reusableFormSchema.parse(source.value)
                                            .fields,
                                        ).mapping,
                                      }
                                    : undefined,
                                });
                              }}
                            >
                              <option value="">This form’s profiles</option>
                              {resources
                                .filter(
                                  (r) =>
                                    r.kind === "form" &&
                                    (scope !== "shared" || !r.projectId) &&
                                    !r.archived &&
                                    r.id !== editing?.id &&
                                    !reusableFormSchema.parse(r.value)
                                      .profileSource,
                                )
                                .map((r) => (
                                  <option
                                    key={`${r.id}:${r.version}`}
                                    value={JSON.stringify([r.id, r.version])}
                                  >
                                    {r.name} · v{r.version}
                                  </option>
                                ))}
                            </Select>
                          </Control>
                          {(() => {
                            const form = value as ReturnType<
                                typeof reusableFormSchema.parse
                              >,
                              link = form.profileSource;
                            const source =
                              link &&
                              resources.find(
                                (r) =>
                                  r.id === link.id &&
                                  r.version === link.version,
                              );
                            return source && link ? (
                              <div className="part-card">
                                <h4>Profile to form mapping</h4>
                                {form.fields.map((input) => (
                                  <Control
                                    key={input.id}
                                    label={`Profile value for ${input.label}`}
                                  >
                                    <Select
                                      value={link.mapping[input.id] || ""}
                                      onChange={(e) => {
                                        const mapping = { ...link.mapping };
                                        if (e.target.value)
                                          mapping[input.id] = e.target.value;
                                        else delete mapping[input.id];
                                        setDraft({
                                          ...form,
                                          profileSource: { ...link, mapping },
                                        });
                                      }}
                                    >
                                      <option value="">No profile value</option>
                                      {reusableFormSchema
                                        .parse(source.value)
                                        .fields.filter((f) =>
                                          formFieldCompatible(input, f),
                                        )
                                        .map((f) => (
                                          <option key={f.id} value={f.id}>
                                            {f.label} ({f.key})
                                          </option>
                                        ))}
                                    </Select>
                                  </Control>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </>
                      )}
                      {["validation", "format"].includes(kind) && (
                        <PolicySettingsEditor
                          kind={kind as "validation" | "format"}
                          value={value as import("@proof/core").Field["format"]}
                          onApply={setDraft}
                        />
                      )}
                      {kind === "rule" && (
                        <RuleLibraryEditor
                          value={value}
                          project={project}
                          onChange={setDraft}
                        />
                      )}
                      {kind === "preset" && (
                        <PresetLibraryEditor
                          value={value}
                          project={project}
                          onChange={setDraft}
                        />
                      )}
                      {kind === "dataset" && (
                        <DatasetEditor
                          key={`${editorGeneration}:${editing?.id || "new"}:${editing?.version || 0}`}
                          value={value as import("@proof/core").Dataset}
                          onChange={setDraft}
                        />
                      )}
                      {kind === "production" && (
                        <>
                          <InsetsControl
                            label="Bleed"
                            max={144}
                            value={productionSchema.parse(value).bleed}
                            onChange={(bleed) =>
                              setDraft({
                                ...productionSchema.parse(value),
                                bleed,
                              })
                            }
                          />
                          <InsetsControl
                            label="Slug"
                            max={144}
                            value={productionSchema.parse(value).slug}
                            onChange={(slug) =>
                              setDraft({
                                ...productionSchema.parse(value),
                                slug,
                              })
                            }
                          />
                          <PageMarks
                            value={productionSchema.parse(value)}
                            onChange={setDraft}
                          />
                        </>
                      )}
                      {kind === "sheet" && (
                        <SheetSettings
                          project={project}
                          output={{
                            ...createOutputProfile("high-res"),
                            imposition: impositionSchema.parse(value),
                          }}
                          onChange={setDraft}
                        />
                      )}
                      {kind === "output" && (
                        <PrintProfileControl
                          key={`${editorGeneration}:${editing?.id || "new"}:${editing?.version || 0}`}
                          value={
                            (value as import("@proof/core").OutputProfile)
                              .printProfile
                          }
                          onChange={(printProfile) =>
                            setDraft({
                              ...(value as import("@proof/core").OutputProfile),
                              printProfile,
                            })
                          }
                        />
                      )}
                    </>
                  ) : (
                    <JsonEditor
                      label="Catalog value JSON"
                      value={value}
                      onSave={async (v) => {
                        if (!(await save(v)))
                          throw new Error(
                            "Resource was not saved. Review the resource name, data and permissions.",
                          );
                      }}
                    />
                  )}
                </div>
              </li>
              <li>
                <span className="setup-step-number" aria-hidden="true">
                  3
                </span>
                <div className="setup-step-body">
                  <h4>Save</h4>
                  {error && <p role="alert">{error}</p>}
                  {visualSetup && setupMode === "guided" ? (
                    <div className="button-row">
                      <Button
                        kind="primary"
                        disabled={busy || !permissions.manage}
                        onClick={() => void save(value)}
                      >
                        {busy ? "Saving…" : "Save library version"}
                      </Button>
                    </div>
                  ) : (
                    <p className="muted">
                      Choose Apply configuration above to save this version.
                    </p>
                  )}
                </div>
              </li>
            </ol>
          </fieldset>
        )}
        <hr />
        {kinds.length === 1 && kinds[0] === "dataset" && (
          <h3>Saved datasets ({latest.length})</h3>
        )}
        <div className="control-grid">
          <Control label="Search library">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Control>
          <Control label="Filter library">
            <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All building blocks</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {labels[k]}
                </option>
              ))}
            </Select>
          </Control>
        </div>
        {!latest.length && (
          <p className="muted">
            Your library is empty. Create a form, dataset, rule, or preset
            above.
          </p>
        )}
        {!!latest.length &&
          !latest.some(
            (row) =>
              (filter === "all" || row.kind === filter) &&
              row.name.toLowerCase().includes(search.toLowerCase()),
          ) && <p className="muted">No building blocks match your search.</p>}
        <div className="catalog-entries">
          {latest
            .filter(
              (row) =>
                (filter === "all" || row.kind === filter) &&
                row.name.toLowerCase().includes(search.toLowerCase()),
            )
            .map((row) => (
              <article className="saved-proof-row" key={row.id}>
                <div>
                  <strong>
                    {row.name} · v{row.version}
                  </strong>
                  <small>
                    {labels[row.kind]}
                    {row.kind === "output" ? " · Global profile" : ""}
                    {row.archived ? " · Archived" : ""}
                  </small>
                </div>
                <div className="button-row">
                  <Button
                    disabled={row.archived || !permissions.manage}
                    onClick={() => {
                      edit(row);
                    }}
                  >
                    Edit new version
                  </Button>
                  {scope === "shared" && row.kind !== "output" && (
                    <Button
                      disabled={
                        row.archived ||
                        !permissions.manage ||
                        !allowProjectScope
                      }
                      onClick={() =>
                        perform(async () => {
                          await repository.saveResource(project.tenantId, {
                            ...row,
                            id: uid("resource"),
                            projectId: project.id,
                            version: 0,
                          });
                          setScope("project");
                          start(row.kind);
                          notify(
                            `Copied ${row.name} into ${project.name}. The shared version is unchanged.`,
                          );
                        })
                      }
                    >
                      Copy to this project
                    </Button>
                  )}
                  {scope === "project" && row.kind !== "output" && (
                    <Button
                      disabled={
                        row.archived || !permissions.manage || !canShare(row)
                      }
                      title={
                        !canShare(row)
                          ? "Select a shared profile collection before sharing this form"
                          : undefined
                      }
                      onClick={() =>
                        perform(async () => {
                          const { projectId: _owner, ...copy } = row;
                          await repository.saveResource(project.tenantId, {
                            ...copy,
                            id: uid("resource"),
                            version: 0,
                          });
                          setScope("shared");
                          start(row.kind);
                          notify(
                            `Created a shared copy of ${row.name}. Project configuration is unchanged.`,
                          );
                        })
                      }
                    >
                      Save shared copy
                    </Button>
                  )}
                  <Button
                    disabled={row.archived || !permissions.apply}
                    onClick={() => apply(row)}
                  >
                    Apply version
                  </Button>
                  <Button
                    disabled={!permissions.manage}
                    onClick={() =>
                      perform(() =>
                        repository.archiveResource(
                          project.tenantId,
                          row.id,
                          !row.archived,
                        ),
                      )
                    }
                  >
                    {row.archived
                      ? "Restore catalog entry"
                      : "Archive catalog entry"}
                  </Button>
                </div>
                <details className="library-version-history">
                  <summary>Version history &amp; export</summary>
                  {resources
                    .filter((r) => r.id === row.id)
                    .sort((a, b) => b.version - a.version)
                    .map((r) => (
                      <div className="button-row" key={r.version}>
                        <span>
                          v{r.version} ·{" "}
                          {r.at ? new Date(r.at).toLocaleString() : "Saved"}
                        </span>
                        <Button
                          disabled={row.archived || !permissions.apply}
                          onClick={() => apply(r)}
                        >
                          Apply v{r.version}
                        </Button>
                        <Button
                          disabled={row.archived || !permissions.manage}
                          onClick={() => edit(r)}
                        >
                          Edit from v{r.version}
                        </Button>
                        <Button onClick={() => download(r)}>
                          Export v{r.version}
                        </Button>
                      </div>
                    ))}
                </details>
              </article>
            ))}
        </div>
        {policy && scope === "shared" && (
          <details>
            <summary>Tenant defaults &amp; draft retention</summary>
            <p>
              New blank documents use these exact versions. Starter documents
              preserve their design. Unlocked backups share one tenant-wide
              quota; manual and unexpired publication locks are excluded.
            </p>
            <Control label="Unlocked draft backup quota">
              <input
                type="number"
                min={0}
                max={1000}
                value={policy.revisionQuota}
                onChange={(e) =>
                  setPolicy({
                    ...policy,
                    revisionQuota: Number(e.target.value),
                  })
                }
              />
            </Control>
            <div className="control-grid">
              {kinds.map((kind) => (
                <Control key={kind} label={`Default ${labels[kind]}`}>
                  <Select
                    value={
                      policy.defaultResources[kind]
                        ? JSON.stringify([
                            policy.defaultResources[kind]!.id,
                            policy.defaultResources[kind]!.version,
                          ])
                        : ""
                    }
                    onChange={(e) => {
                      const defaults = { ...policy.defaultResources };
                      if (e.target.value) {
                        const [id, version] = JSON.parse(e.target.value);
                        defaults[kind] = { id, version };
                      } else delete defaults[kind];
                      setPolicy({ ...policy, defaultResources: defaults });
                    }}
                  >
                    <option value="">No catalog default</option>
                    {resources
                      .filter(
                        (r) => r.kind === kind && !r.archived && !r.projectId,
                      )
                      .map((r) => (
                        <option
                          key={`${r.id}:${r.version}`}
                          value={JSON.stringify([r.id, r.version])}
                        >
                          {r.name} · v{r.version}
                        </option>
                      ))}
                  </Select>
                </Control>
              ))}
            </div>
            <Button
              onClick={() =>
                perform(async () => {
                  await repository.saveTenantSettings(
                    project.tenantId,
                    policy,
                    policy.revision,
                  );
                  notify("Tenant defaults and retention policy saved.");
                })
              }
            >
              Save tenant policy
            </Button>
          </details>
        )}
      </fieldset>
      {assignment && (
        <GlobalFormAssignment
          key={`${assignment.id}:${assignment.version}`}
          project={project}
          resource={assignment}
          onClose={() => setAssignment(undefined)}
          onApply={(p) => {
            const link = reusableFormSchema.parse(
              assignment.value,
            ).profileSource;
            const source =
              link &&
              resources.find(
                (r) => r.id === link.id && r.version === link.version,
              );
            if (
              source &&
              !p.resourceSnapshots.some(
                (r) => r.id === source.id && r.version === source.version,
              )
            )
              p.resourceSnapshots.push(source);
            onChange(p);
            setAssignment(undefined);
            notify(
              "Form assigned. Review mappings under Variables → Reusable forms.",
            );
          }}
        />
      )}
    </section>
  );
}
